<template>
	<div
		class="best-price-calendar-container"
		@click.once="fetchOffers()"
	>
		<Collapse
			class="best-price-calendar-container__collapse"
			:keep-alive="true"
			@Collapse:Toggle="handleCollapseToggle"
		>
			<template #header>
				<div>
					<h2 class="best-price-calendar-container__title">
						<BaseIcon
							name="calendar"
							width="22"
							height="24"
						/>
						<div class="best-price-calendar-container__title-text">Sparkalender</div>
					</h2>
					<div class="best-price-calendar-container__subtitle">Finden Sie günstigere Angebote mit flexiblem Abreisedatum</div>
				</div>
			</template>
			<template #default>
				<div class="collapse-body">
					<div>
						<Loading
							v-if="beforeInitialFetch"
							unique-key="calendar-loader"
							class="best-price-calendar-container__loading-icon"
						/>
						<BestPriceCalendar
							v-else-if="offers.length"
							ref="bestPriceCalendar"
							:offers="offers"
							:start-date="fromDate"
							:loading="loading"
							@changeMonth="changeMonth"
							@selectDay="onSelect"
						/>
						<div
							v-else
							class="best-price-calendar-container__no-offers"
						>
							Mit den eingegebenen Sucheinstellungen hat der Kalendar keine Angebote erhalten.
						</div>
					</div>
				</div>
			</template>
		</Collapse>
		<BestPriceSelection
			:filter="filter"
			@deleteFilter="onClearSelection"
		/>
	</div>
</template>

<script lang="ts" setup>
import { requestCancelable } from '@components/common/services/request';
import type { BestPackageOfferForDepartureDateResponse } from '@interfaces/api/v1-best-package-offer-for-departure-date';
import type {
	BestAccommodationOfferForCheckInDate,
	BestAccommodationOfferForCheckInDateResponse,
} from '@interfaces/api/v1-best-accommodation-offer-for-check-in-date';
import { scrollIt, logErrorResponse } from '@utils/utils';
import { now, dateDiff } from '@/js/utils/dateTimeUtils';
import { EventBus } from '@global-js/event-bus';
import BestPriceCalendar from '@lmt-rpb/BestPriceCalendar/BestPriceCalendar.vue';
import BestPriceSelection from '@lmt-rpb/BestPriceSelection/BestPriceSelection.vue';
import { useStore } from '@/components/common/store';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { SearchFormDataType } from '@/interfaces/search-form';
import Collapse from '@lmt-rpb/Collapse/Collapse.vue';
import BaseIcon from '@lmt-rpb/BaseIcon/BaseIcon.vue';
import Loading from '@lmt-rpb/Loading/Loading.vue';
import type { MonthData } from '@helper/types/commonProps';
import { storeToRefs } from 'pinia';
import { useBreakpointStore } from 'src/store/breakpointsStore';
import SelectContentAnalytics from '@modules/analytics/SelectContentAnalytics';
import { API_URLS } from '@global-js/constants';

const store = useStore();

const bestPriceCalendar = ref<InstanceType<typeof BestPriceCalendar>>();

const offers = ref<BestAccommodationOfferForCheckInDate[]>([]);

const loadedOffersFrom = ref<Date>();

const loadedOffersTo = ref<Date>();

const nowDate = ref<Date>(now() as Date);

const loading = ref(false);

const beforeInitialFetch = ref(true);

const formData = computed((): SearchFormDataType => store.state.searchMask);

const apiData = store.getters['searchMask/apiData'];

const savedFormData = ref({ ...formData.value }); // Copy of formData

const fromDate = computed(() => new Date(formData.value.offerDuration.from as number));

const toDate = computed((): Date => new Date(formData.value.offerDuration.to as number));

const offerType = computed((): 'package' | 'hotel' => (formData.value.onlyHotel ? 'hotel' : 'package'));

const travelDuration = computed((): number => formData.value.travelDuration?.[0] || 7);

const filter = computed({
	get() {
		return formData.value.filter;
	},

	set(date: string | undefined | null) {
		store.commit('searchMask/updateFormData', {
			filter: date,
		});
	},
});

const { isDesktop } = storeToRefs(useBreakpointStore());

const getDateRange = (startDate: Date, rangeLength: number): { from: Date; to: Date } => {
	const from = dateDiff(startDate, nowDate.value) < 0 ? startDate : nowDate.value;
	const to = new Date(startDate.getFullYear(), startDate.getMonth() + rangeLength, travelDuration.value);
	return { from, to };
};

const initialOfferInterval = computed((): { from: Date; to: Date } => {
	const firstDay = new Date(fromDate.value.getFullYear(), fromDate.value.getMonth() - 1, 1);
	const monthsToLoad = Math.min(Math.max(dateDiff(firstDay, toDate.value, 'month'), 4), 6);
	return getDateRange(firstDay, monthsToLoad);
});

const onSelect = (date: string) => {
	setTimeout(() => {
		scrollIt(document.querySelector('.offers__list') as HTMLElement, 1000, 'easeOutQuad', isDesktop.value ? 400 : 200);
	}, 250);
	if (filter.value === date) {
		return;
	}

	// update filter
	filter.value = date;
	EventBus.$emit('SearchMask:MobileSubmit');
};

const onClearSelection = () => {
	bestPriceCalendar.value?.clearSelection();
	// remove filter
	filter.value = '';
	EventBus.$emit('SearchMask:MobileSubmit');
};

const formDataWasChanged = (): boolean => {
	// Excludes for offerlist update
	/* eslint-disable @typescript-eslint/no-unused-vars */
	const {
		destination: currentDestination,
		searchTerm: currentSearchTerm,
		modified: currentModified,
		filter: currentFilter,
		...newFormData
	} = formData.value;

	const {
		destination: savedDestination,
		searchTerm: savedtSearchTerm,
		modified: savedModified,
		filter: savedFilter,
		...newSavedFormData
	} = savedFormData.value;
	/* eslint-enable @typescript-eslint/no-unused-vars */

	// Update savedFormData
	savedFormData.value = { ...formData.value };

	// Check if wanted fields updated
	if (JSON.stringify(newFormData) !== JSON.stringify(newSavedFormData)) {
		return true;
	}

	return false;
};

const updateLoadedDateRange = (start: Date, end: Date) => {
	loadedOffersFrom.value =
		loadedOffersFrom.value === undefined || dateDiff(start, loadedOffersFrom.value) > 0 ? start : loadedOffersFrom.value;
	loadedOffersTo.value = loadedOffersTo.value === undefined || dateDiff(end, loadedOffersTo.value) < 0 ? end : loadedOffersTo.value;
};

const getRequestData = (start: Date, end: Date) => {
	const exclude = offerType.value === 'hotel' ? ['DepartureAirport', 'DirectFlight', 'DepartureTime', 'ArrivalTime'] : [];
	// Removing params which are at the moment specific to the hotellist request
	exclude.push(
		'Regions',
		'Cities',
		'MaxPrice',
		'SortOrder',
		'HotelCategory',
		'MinMeanRecommendationRate',
		'RatingAttributes',
		'HotelAttributes',
		'Countries'
	);
	const body = apiData(
		{
			offerDuration: {
				from: start.getTime(),
				to: end.getTime(),
			},
		},
		exclude
	);
	return body;
};

const fetchOffers = async (start: Date = initialOfferInterval.value.from, end: Date = initialOfferInterval.value.to) => {
	const url = API_URLS.OFFER_CALENDAR[formData.value.onlyHotel ? 'HOTEL' : 'PACKAGE'];
	const requestDate = getRequestData(start, end);
	updateLoadedDateRange(start, end);
	loading.value = true;
	try {
		const resp = await requestCancelable<BestAccommodationOfferForCheckInDateResponse | BestPackageOfferForDepartureDateResponse>(
			{ url, data: requestDate },
			'bestPriceBox'
		);
		if (!resp || !resp.Offers) {
			return;
		}
		offers.value.push(...resp.Offers);
	} catch (error) {
		logErrorResponse('Best Accommodation Offer For Check In Date Response:', error, requestDate, offers.value);
	} finally {
		loading.value = false;
		beforeInitialFetch.value = false;
	}
};

const handleMobileSubmit = () => {
	if (!isDesktop.value && formDataWasChanged()) {
		const { from, to } = initialOfferInterval.value;
		offers.value = [];
		fetchOffers(from, to);
	}
};

const addMonthRight = (monthData: MonthData) => {
	const newMonthStart = new Date(monthData.year, monthData.month + 1, 1);
	const { from, to } = getDateRange(newMonthStart, 1);
	if (loadedOffersTo.value && dateDiff(loadedOffersTo.value, to) > 0) {
		fetchOffers(from, to);
	}
};

const addMonthLeft = (monthData: MonthData) => {
	const newMonth = new Date(monthData.year, monthData.month, 1);
	if (loadedOffersFrom.value && dateDiff(newMonth, loadedOffersFrom.value) > 0) {
		const { from, to } = getDateRange(newMonth, 1);
		fetchOffers(from, to);
	}
};

const changeMonth = (monthData: MonthData) => {
	addMonthRight(monthData);
	addMonthLeft(monthData);
};

const handleCollapseToggle = (isOpen: boolean) => {
	if (isOpen) {
		SelectContentAnalytics.selectContent({
			cId: 'best_price_calendar',
			cType: 'ux_hotelpage_view',
		});
	}
};

onMounted(() => {
	EventBus.$on('SearchMask:MobileSubmit', handleMobileSubmit);
});

onBeforeUnmount(() => {
	EventBus.$off('SearchMask:MobileSubmit', handleMobileSubmit);
});

watch(
	() => formData.value,
	() => {
		if (isDesktop.value && formDataWasChanged()) {
			offers.value = [];
			const { from, to } = initialOfferInterval.value;
			loadedOffersFrom.value = from;
			loadedOffersTo.value = to;
			fetchOffers(from, to);
		}
	},
	{ deep: true }
);
</script>

<style lang="scss" scoped>
.best-price-calendar-container {
	margin-bottom: 1.2rem;
	background: white;

	@media (min-width: $breakpoint-large) {
		margin-bottom: 2.4rem;
	}

	&__collapse {
		margin-bottom: 1.2rem;
		box-shadow: $box-shadow;
		border-radius: $border-radius-small;
	}

	&__loading-icon {
		width: 100% !important;
	}

	:deep(.collapse__header) {
		padding: 1.8rem 3.6rem;
		color: $color-primary;
		cursor: pointer;
		align-items: center;
		justify-content: space-between;
	}

	:deep(.collapse__body) {
		border-top: 1px solid $color-primary-l4;
		color: black;
		padding: 0 1rem;
	}

	:deep(.collapse__icon) {
		width: 2.5rem;
	}

	&__title {
		display: flex;
		align-items: center;
		gap: 1.2rem;
		font-size: $font-small-1;
		font-weight: $font-weight-semibold;
		font-family: $font-family;
		margin: 0;
		padding: 0;
		text-align: left;
		fill: $color-primary;
	}

	&__subtitle {
		margin-left: 3.4rem;
		font-size: $font-small-2;
	}

	&__no-offers {
		padding: 5.5rem 3.6rem; // box should have 130px height
	}

	@media (min-width: $breakpoint-medium) {
		&__title {
			font-size: $font-medium-3;
		}

		&__subtitle {
			font-size: $font-small-1;
		}
	}
}
</style>
